import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { pingApiPingGetOptions } from "../generated/api/@tanstack/react-query.gen";
import { runGenerate, transcribeAudioFile } from "../lib/home-page-actions";
import { MAX_REFERENCE_IMAGES } from "../lib/image-constants";
import { downscaleImageFile } from "../lib/image-input";
import type { ReferenceAttachment } from "../lib/reference-attachment";
import { useImageDropZone } from "./useImageDropZone";
import { useLatestPick } from "./useLatestPick";
import { useSpeechToText } from "./useSpeechToText";

function useSpeechSetup(
  setPrompt: React.Dispatch<React.SetStateAction<string>>,
  setError: (err: string | null) => void,
) {
  return useSpeechToText({
    onResult: (transcript) => {
      if (transcript.trim())
        setPrompt((prev) => prev + (prev.trim() ? " " : "") + transcript);
    },
    onError: (err) => {
      if (err && !err.includes("no-speech"))
        setError(`Speech recognition error: ${err}`);
    },
  });
}

function useImageSelection(isGenerating: boolean) {
  const [references, setReferences] = useState<ReferenceAttachment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [incomingFile, setIncomingFile] = useState<File | null>(null);
  const atCapacity = references.length >= MAX_REFERENCE_IMAGES;

  // A drop still being downscaled must not open the modal over one the user
  // has since opened or closed themselves.
  const latestDrop = useLatestPick();
  const openReferenceModal = () => {
    latestDrop.cancel();
    setIncomingFile(null);
    setIsModalOpen(true);
  };
  // Paste and drop don't attach on their own. The modal opens on the upload
  // tab with this file already chosen, and attaching waits for Add. The file
  // is downscaled first, like one picked inside the modal.
  const stageIncomingImage = (file: File) => {
    if (atCapacity) return;
    const isLatest = latestDrop.begin();
    downscaleImageFile(file).then((scaled) => {
      if (!isLatest()) return;
      setIncomingFile(scaled);
      setIsModalOpen(true);
    });
  };
  const closeReferenceModal = () => {
    latestDrop.cancel();
    setIsModalOpen(false);
    setIncomingFile(null);
  };
  const addReference = (attachment: ReferenceAttachment) => {
    setReferences((prev) =>
      prev.length >= MAX_REFERENCE_IMAGES ? prev : [...prev, attachment],
    );
  };
  const handleReferenceImageRemove = (index: number) => {
    setReferences((prev) => prev.filter((_, i) => i !== index));
  };
  const dropZoneProps = useImageDropZone({
    onFile: stageIncomingImage,
    disabled: isGenerating || isModalOpen || atCapacity,
  });

  return {
    references,
    isModalOpen,
    incomingFile,
    openReferenceModal,
    closeReferenceModal,
    addReference,
    handleReferenceImageRemove,
    dropZoneProps,
    stageIncomingImage,
  };
}

function useAuthCheck() {
  return useQuery({
    ...pingApiPingGetOptions(),
    retry: (failureCount, err: AxiosError) => {
      if (
        err instanceof AxiosError &&
        (err.response?.status === 401 || err.response?.status === 403)
      )
        return false;
      return failureCount < 2;
    },
  });
}

interface HomePageState {
  prompt: string;
  isGenerating: boolean;
  projectId: string | undefined;
  references: ReferenceAttachment[];
  navigate: ReturnType<typeof useNavigate>;
  setIsGenerating: (v: boolean) => void;
  setError: (err: string | null) => void;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  setIsTranscribing: (v: boolean) => void;
  audioInputRef: React.RefObject<HTMLInputElement | null>;
}

function useHomePageHandlers({
  prompt,
  isGenerating,
  projectId,
  references,
  navigate,
  setIsGenerating,
  setError,
  setPrompt,
  setIsTranscribing,
  audioInputRef,
}: HomePageState) {
  const handleGenerate = () =>
    runGenerate({
      prompt,
      projectId,
      references,
      navigate,
      setIsGenerating,
      setError,
    });
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isGenerating && prompt.trim()) handleGenerate();
    }
  };
  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    await transcribeAudioFile({
      audioFile: e.target.files[0],
      setPrompt,
      setError,
      setIsTranscribing,
      audioInputRef,
    });
  };
  return { handleGenerate, handleKeyDown, handleAudioUpload };
}

export interface InputState {
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export interface AudioState {
  isTranscribing: boolean;
  isListening: boolean;
  isSpeechSupported: boolean;
  toggleSpeech: () => void;
  audioInputRef: React.RefObject<HTMLInputElement | null>;
  handleAudioUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface GenerationState {
  isGenerating: boolean;
  error: string | null;
  handleGenerate: () => void;
}

export interface AuthState {
  isLoading: boolean;
  authError: unknown;
}

export interface DropZoneState {
  dropZoneProps: ReturnType<typeof useImageDropZone>;
  references: ReferenceAttachment[];
  openReferenceModal: () => void;
  closeReferenceModal: () => void;
  addReference: (attachment: ReferenceAttachment) => void;
  isReferenceModalOpen: boolean;
  /** File a paste or drop should pre-fill on the upload tab. */
  incomingFile: File | null;
  handleReferenceImageRemove: (index: number) => void;
  /** Opens the asset modal with a just-pasted or dropped image. */
  stageIncomingImage: (file: File) => void;
}

function useHomePageInputState() {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const {
    isListening,
    isSupported: isSpeechSupported,
    toggle: toggleSpeech,
  } = useSpeechSetup(setPrompt, setError);
  const {
    references,
    isModalOpen,
    incomingFile,
    openReferenceModal,
    closeReferenceModal,
    addReference,
    handleReferenceImageRemove,
    dropZoneProps,
    stageIncomingImage,
  } = useImageSelection(isGenerating);
  return {
    prompt,
    setPrompt,
    isGenerating,
    setIsGenerating,
    error,
    setError,
    isTranscribing,
    setIsTranscribing,
    audioInputRef,
    isListening,
    isSpeechSupported,
    toggleSpeech,
    references,
    isModalOpen,
    incomingFile,
    openReferenceModal,
    closeReferenceModal,
    addReference,
    handleReferenceImageRemove,
    dropZoneProps,
    stageIncomingImage,
  };
}

export function useHomePage(projectId: string | undefined) {
  const navigate = useNavigate();
  const state = useHomePageInputState();
  const { isLoading, error: authError } = useAuthCheck();
  const { handleGenerate, handleKeyDown, handleAudioUpload } =
    useHomePageHandlers({
      ...state,
      navigate,
      projectId,
    });
  return {
    input: {
      prompt: state.prompt,
      setPrompt: state.setPrompt,
      handleKeyDown,
    },
    generation: {
      isGenerating: state.isGenerating,
      error: state.error,
      handleGenerate,
    },
    audio: {
      isTranscribing: state.isTranscribing,
      isListening: state.isListening,
      isSpeechSupported: state.isSpeechSupported,
      toggleSpeech: state.toggleSpeech,
      audioInputRef: state.audioInputRef,
      handleAudioUpload,
    },
    dropZone: {
      dropZoneProps: state.dropZoneProps,
      references: state.references,
      openReferenceModal: state.openReferenceModal,
      closeReferenceModal: state.closeReferenceModal,
      addReference: state.addReference,
      isReferenceModalOpen: state.isModalOpen,
      incomingFile: state.incomingFile,
      handleReferenceImageRemove: state.handleReferenceImageRemove,
      stageIncomingImage: state.stageIncomingImage,
    },
    auth: {
      isLoading,
      authError,
    },
  };
}
